import { ReactNode } from "react";
import { SettingsAccordion } from "./SettingsAccordion";
import { SectionHeader, SettingRow } from "./BooksSettings";
import { HelperText } from "components/DesignSystem/HelperText/HelperText";
import { Settings } from "components/icons/Navbar/Settings";
import {
  SECTION_ORDER,
  SECTION_LABELS,
  DEFAULT_CATEGORIES_SECTIONS,
} from "constants/booksSettings";
import { BooksSettingLines } from "components/icons/Navbar/BooksSettingLines";
import { Payroll } from "components/icons/Payroll";
import { MultiCurrency } from "components/icons/MultiCurrency";
import { Expense } from "components/icons/Expense";
import Stripe from "static/images/Stripe.svg";

type DefaultCategoryRow = {
  section: string;
  title: string;
  category_name: string;
  coa_description: string;
  category?: unknown;
};

type DefaultCategoriesAccordionProps<T extends DefaultCategoryRow> = {
  categoryData: T[];
  renderSelector: (row: T) => ReactNode;
  requiredCategoryNames?: string[];
};

const EMPTY_REQUIRED_CATEGORY_NAMES: string[] = [];

const getSectionIcon = (section: string) => {
  switch (section) {
    case DEFAULT_CATEGORIES_SECTIONS.PAYROLL:
      return <Payroll className="t-size-3.5" />;
    case DEFAULT_CATEGORIES_SECTIONS.REVENUE:
      return <Settings className="t-size-3.5" />;
    case DEFAULT_CATEGORIES_SECTIONS.STRIPE:
      return <img src={Stripe} alt="Stripe" className="t-size-3.5" />;
    case DEFAULT_CATEGORIES_SECTIONS.EXPENSE:
      return <Expense className="t-size-3.5" />;
    case DEFAULT_CATEGORIES_SECTIONS.MULTICURRENCY:
      return <MultiCurrency className="t-size-3.5" />;
    case DEFAULT_CATEGORIES_SECTIONS.ADJUSTMENTS:
      return <BooksSettingLines className="t-size-3.5" />;
    default:
      return <Settings className="t-size-3.5" />;
  }
};

export const DefaultCategoriesAccordion = <T extends DefaultCategoryRow>({
  categoryData,
  renderSelector,
  requiredCategoryNames = EMPTY_REQUIRED_CATEGORY_NAMES,
}: DefaultCategoriesAccordionProps<T>) => {
  const groupedCategories = categoryData.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {} as Record<string, T[]>);

  const sections = SECTION_ORDER.filter(
    (section) => groupedCategories[section]
  );

  const defaultOpenSections = sections.length > 0 ? [sections[0]] : [];

  return (
    <SettingsAccordion.Root defaultValue={defaultOpenSections}>
      {sections.map((section) => (
        <SettingsAccordion.Item key={section} value={section}>
          <SettingsAccordion.Trigger>
            <SectionHeader
              icon={getSectionIcon(section)}
              title={SECTION_LABELS[section] || section}
            />
          </SettingsAccordion.Trigger>

          <SettingsAccordion.Content>
            {groupedCategories[section].map((row) => {
              const isRequired = requiredCategoryNames.includes(
                row.category_name
              );
              const isMissingRequired = isRequired && !row.category;
              const title = isRequired ? `${row.title} *` : row.title;

              return (
                <SettingRow
                  key={row.category_name}
                  title={title}
                  description={row.coa_description}
                  info={
                    isMissingRequired && (
                      <HelperText variant="error">
                        Select a category to continue
                      </HelperText>
                    )
                  }
                >
                  <div className="t-w-56 sm:t-w-full">
                    {renderSelector(row)}
                  </div>
                </SettingRow>
              );
            })}
          </SettingsAccordion.Content>
        </SettingsAccordion.Item>
      ))}
    </SettingsAccordion.Root>
  );
};
